import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { ContractorShell } from "../components/ContractorShell";
import { SchemaPrint } from "../components/SchemaPrint";
import { SCHEMAS, permitMeta } from "../lib/permitSchemas";
import logo from "../assets/gfm-logo.png";
import { Button } from "../components/ui/button";
import { ArrowLeft, Printer, Loader2, Ban } from "lucide-react";

function fmt(dt) {
  if (!dt) return "—";
  try {
    return new Date(dt).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return dt; }
}

export default function ContractorPermitView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [permit, setPermit] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/contractor/permits/${id}`).then((r) => setPermit(r.data)).catch(() => setNotFound(true));
  }, [id]);

  if (notFound)
    return (
      <ContractorShell>
        <div className="mx-auto max-w-md rounded-xl border border-border bg-white p-8 text-center" data-testid="permit-not-found">
          <p className="text-sm text-muted-foreground">This permit could not be found or is not linked to your account.</p>
          <Button className="mt-4 bg-brand hover:bg-brand-dark" onClick={() => navigate("/contractor")}>Back to portal</Button>
        </div>
      </ContractorShell>
    );

  if (!permit)
    return <ContractorShell><div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div></ContractorShell>;

  return (
    <>
      <div className="print-hidden">
        <ContractorShell>
          <button onClick={() => navigate("/contractor")} className="mb-4 flex items-center gap-1 text-sm text-muted-foreground hover:text-brand" data-testid="back-btn">
            <ArrowLeft className="h-4 w-4" /> Back to portal
          </button>
          <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-brand">{permit.permit_number}</div>
              <h1 className="font-heading text-3xl font-bold tracking-tight">{permit.contractor_name}</h1>
              <p className="text-muted-foreground">{permit.company} · {permit.site_location}</p>
              {permit.cancelled_at && (
                <span className="mt-2 inline-flex items-center gap-1 rounded-full bg-red-100 px-3 py-1 text-xs font-semibold text-red-700" data-testid="cancelled-badge">
                  <Ban className="h-3.5 w-3.5" /> Cancelled {fmt(permit.cancelled_at)}
                </span>
              )}
            </div>
            <Button className="bg-brand hover:bg-brand-dark" onClick={() => window.print()} data-testid="download-permit-btn">
              <Printer className="mr-2 h-4 w-4" /> Download / Print PDF
            </Button>
          </div>
          <div className="rounded-md border border-border bg-white p-4 text-sm shadow-sm sm:max-w-md">
            <Row label="Issued" value={fmt(permit.issue_at)} />
            <Row label="Expires" value={fmt(permit.expiry_at)} />
            <Row label="Authorised by" value={permit.issuer_name} />
            <Row label="Person in Charge" value={permit.pic_name || "—"} />
          </div>
          <p className="mt-3 text-xs text-muted-foreground">Tip: click "Download / Print PDF", then choose "Save as PDF" as the destination to keep a copy.</p>
        </ContractorShell>
      </div>

      <div className="print-only">
        {(permit.permit_types || []).map((t, i) => (
          <div key={t} className={`a4-sheet permit-print relative bg-white ${i > 0 ? "page-break" : ""}`}>
            {permit.cancelled_at && (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <span className="rotate-[-30deg] rounded border-4 border-red-500 px-8 py-2 text-5xl font-extrabold uppercase tracking-widest text-red-500 opacity-30">Cancelled</span>
              </div>
            )}
            <PrintHeader permit={permit} schema={SCHEMAS[t]} />
            <div className="permit-cols">
              <SchemaPrint schema={SCHEMAS[t]} data={permit.permit_data?.[t] || {}} />
            </div>
            <Signatures permit={permit} />
          </div>
        ))}
      </div>
    </>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between border-b border-border py-1.5 last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}

function PrintHeader({ permit, schema }) {
  return (
    <div className="mb-4 border-b-2 border-brand pb-3">
      <div className="flex items-start justify-between">
        <div className="text-[10px] leading-tight text-neutral-600">
          Level 3 : Quality Operations Procedures<br />
          Type : GFM Compliance Manual — Section 41<br />
          Name : {schema.title} · Sponsor : Chief Engineer
        </div>
        <img src={logo} alt="GFM" className="h-9" />
      </div>
      <div className="mt-3 flex items-end justify-between">
        <h1 className="font-heading text-2xl font-extrabold uppercase tracking-tight text-brand">{schema.title}</h1>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-neutral-500">Serial Number</div>
          <div className="font-heading text-xl font-bold text-brand">{permit.permit_number}</div>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1 text-[12px]">
        <div><b>Name:</b> {permit.contractor_name}</div>
        <div><b>Company:</b> {permit.company}</div>
        <div><b>ID No:</b> {permit.id_number}</div>
        <div><b>Mobile:</b> {permit.contractor_mobile || "—"}</div>
        <div><b>Site:</b> {permit.site_location}</div>
        <div><b>Valid from:</b> {fmt(permit.issue_at)}</div>
        <div><b>Expiry:</b> {fmt(permit.expiry_at)}</div>
      </div>
    </div>
  );
}

function Signatures({ permit }) {
  const cl = permit.clearance;
  return (
    <div className="no-break mt-6 border-t-2 border-brand pt-4">
      <div className="grid grid-cols-2 gap-6">
        <SigBlock title="Authorised Person" name={permit.issuer_name} sub={permit.issuer_job_title} sig={permit.issuer_signature} date={permit.issue_at} />
        <SigBlock title="Person in Charge" name={permit.pic_name} sig={permit.pic_signature} date={permit.issue_at} />
      </div>
      <div className="mt-4 rounded border border-neutral-400 p-2">
        <div className="text-[11px] font-bold uppercase tracking-wider text-brand">Completion sign-off</div>
        <div className="mb-2 text-[11px]">
          <span className="mr-1 inline-flex h-3.5 w-3.5 items-center justify-center border border-neutral-500 text-[9px] font-bold align-middle">{cl?.area_clean ? "\u2713" : ""}</span>
          The work area(s) have been left clean and tidy and all waste removed.
        </div>
        <div className="grid grid-cols-2 gap-6">
          <SigBlock title="Person in Charge" name={cl?.pic_name || permit.pic_name} sig={cl?.pic_signature} date={cl?.pic_date} />
          <SigBlock title="GFM Appointed Person" name={cl?.gfm_name} sig={cl?.gfm_signature} date={cl?.gfm_datetime} />
        </div>
      </div>
    </div>
  );
}

function SigBlock({ title, name, sub, sig, date }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-brand">{title}</div>
      <div className="mt-1 h-14 border-b border-neutral-400">
        {sig && <img src={sig} alt="signature" className="h-14 object-contain" />}
      </div>
      <div className="mt-1 text-[12px] font-semibold">{name || "—"}</div>
      {sub && <div className="text-[10px] text-neutral-500">{sub}</div>}
      <div className="text-[10px] text-neutral-500">Date: {fmt(date)}</div>
    </div>
  );
}
